import { EditRecordButton } from "@/components/explorer/RecordEditor";
import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/shared/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import * as Icons from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { useCollection } from "@/hooks/useCollection";
import { getFunctions, getFunctionLogs } from "@/services/adminApi";
import { text } from "@/lib/format";

const stateCls = (s) => s === "ACTIVE" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200";
const ago = (t) => { try { return formatDistanceToNowStrict(new Date(t), { addSuffix: true }); } catch { return "—"; } };

// Client-side lead-fanout investigation (no backend dependency): a request that
// received 0 lead_distributions is a "0 pros notified" case.
function useFanout() {
  const requests = useCollection("requests");
  const leads = useCollection("lead_distributions");
  const pros = useCollection("professionals");
  return useMemo(() => {
    const byReq = {};
    leads.forEach((l) => { byReq[l.request_id] = (byReq[l.request_id] || 0) + 1; });
    const activePros = pros.filter((p) => p.status === "active");
    const cities = new Set(); pros.forEach((p) => { (p.service_cities || p.cities || []).forEach((c) => c && cities.add(c)); if (p.city) cities.add(p.city); });
    const zero = requests.filter((r) => (byReq[r.id] || 0) === 0).map((r) => ({
      id: r.id, title: text(r.title), city: text(r.city), internal_status: text(r.internal_status),
      likely_reason: cities.has(r.city) ? "No active pro responded / matched" : `No active pro serves "${text(r.city)}"`,
    }));
    return { total: requests.length, zero, activePros: activePros.length, cities: [...cities] };
  }, [requests, leads, pros]);
}

export default function Deployments() {
  const [fns, setFns] = useState([]);
  const [fnError, setFnError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logsFor, setLogsFor] = useState(null);
  const [logs, setLogs] = useState([]);
  const inv = useFanout();

  const load = async () => {
    setLoading(true); setFnError(null);
    try { setFns(await getFunctions()); }
    catch (e) { setFnError(e?.response?.data?.detail || "Cloud Functions health is temporarily unavailable — the backend admin credential needs renewal."); setFns([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openLogs = async (name) => { setLogsFor(name); setLogs([]); try { setLogs(await getFunctionLogs(name, 40)); } catch { setLogs([{ text: "Logs unavailable (backend credential).", severity: "ERROR" }]); } };

  return (
    <div>
      <PageHeader title="Deployments & Health" subtitle="Cloud Functions health from the shared Firebase project, plus a live lead-fanout investigation computed directly from Firestore."
        actions={<Button size="sm" variant="outline" className="gap-1.5" onClick={load} data-testid="refresh-deployments"><Icons.RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>} />

      <Card className="mb-6">
        <div className="p-4 border-b border-border"><h3 className="font-heading font-semibold text-foreground flex items-center gap-2"><Icons.ServerCog className="h-4 w-4 text-cyan-600" /> Cloud Functions {fns.length ? `(${fns.length})` : ""}</h3></div>
        {fnError ? (
          <div className="p-6 flex items-start gap-3" data-testid="deployments-unavailable">
            <Icons.CloudOff className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground">Cloud Functions health unavailable</p>
              <p className="text-sm text-muted-foreground mt-1">{fnError}</p>
              <p className="text-xs text-muted-foreground mt-2">This section reads deployment metadata via a server-side Google credential. All other Command Center data below and across the app is live and unaffected.</p>
            </div>
          </div>
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Function</TableHead><TableHead>Region</TableHead><TableHead>Trigger</TableHead><TableHead>Runtime</TableHead><TableHead>State</TableHead><TableHead>Last deployed</TableHead><TableHead className="text-right">Logs</TableHead></TableRow></TableHeader>
            <TableBody>
              {fns.map((f) => (
                <TableRow key={f.name + f.region} data-testid={`fn-row-${f.name}-${f.region}`}>
                  <TableCell><span className="font-medium text-foreground">{f.name}</span>{f.duplicate && <span className="ml-2 text-[10px] font-mono text-amber-700 border border-amber-200 rounded px-1">duplicate</span>}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{f.region}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{f.trigger}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{f.runtime}</TableCell>
                  <TableCell><span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${stateCls(f.state)}`}>{f.state}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{ago(f.update_time)}</TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => openLogs(f.name)} data-testid={`fn-logs-${f.name}`}>View</Button></TableCell>
                </TableRow>
              ))}
              {fns.length === 0 && !loading && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No functions.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </Card>

      <Card data-testid="fanout-investigation">
        <div className="p-4 border-b border-border">
          <h3 className="font-heading font-semibold text-foreground flex items-center gap-2"><Icons.Radar className="h-4 w-4 text-primary" /> Lead Fanout Investigation</h3>
          <p className="text-xs text-muted-foreground mt-1">{inv.total} requests · <span className="text-red-600 font-medium">{inv.zero.length} with 0 leads floated</span> · {inv.activePros} active pros · serves {inv.cities.length} cities</p>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>Request</TableHead><TableHead>City</TableHead><TableHead>Internal</TableHead><TableHead>Likely reason</TableHead></TableRow></TableHeader>
          <TableBody>
            {inv.zero.slice(0, 100).map((r) => (
              <TableRow key={r.id} data-testid={`zero-req-${r.id}`}>
                <TableCell><div className="flex items-center gap-1"><div><p className="text-sm text-foreground">{r.title}</p><p className="text-[11px] font-mono text-muted-foreground">{r.id}</p></div><EditRecordButton path="requests" id={r.id} /></div></TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.city}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.internal_status}</TableCell>
                <TableCell className="text-xs text-amber-700">{r.likely_reason}</TableCell>
              </TableRow>
            ))}
            {inv.zero.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-emerald-600">Every request floated at least one lead. ✓</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      {logsFor && (
        <Card className="mt-4" data-testid="fn-log-panel">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <h4 className="font-heading font-semibold text-sm text-foreground flex items-center gap-2"><Icons.Terminal className="h-4 w-4 text-cyan-600" /> Logs · {logsFor}</h4>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setLogsFor(null)}><Icons.X className="h-4 w-4" /></Button>
          </div>
          <div className="p-3 max-h-80 overflow-y-auto bg-secondary font-mono text-[11px] space-y-1">
            {logs.length === 0 ? <p className="text-muted-foreground">Loading…</p> : logs.map((l, i) => (
              <div key={i} className="flex gap-2"><span className="text-muted-foreground shrink-0">{(l.timestamp || "").slice(11, 19)}</span><span className={l.severity === "ERROR" ? "text-red-600" : "text-foreground/80"}>{text(l.text)}</span></div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
